import React, { useMemo } from 'react';
import { Alert, Descriptions, Empty, Space, Tabs, Typography } from 'antd';
import { WBS_LAYER_HINTS } from '@/utils/est3WbsLayers';
import { Est3WbsLayerTag } from '@/components/epc/shared/Est3WbsLayerTag';
import { EpcWbsLibraryCostTemplateGrid } from './EpcWbsLibraryCostTemplateGrid';

const { Text, Paragraph } = Typography;

export interface EpcWbsLibraryNodeInspectorProps {
  packageId: string;
  packageCode?: string;
  nodes: any[];
  selectedNodeId?: string;
  readOnly?: boolean;
  compact?: boolean;
}

export const EpcWbsLibraryNodeInspector: React.FC<EpcWbsLibraryNodeInspectorProps> = ({
  packageId,
  nodes,
  selectedNodeId,
  readOnly,
  compact,
}) => {
  const selected = useMemo(
    () => nodes.find((n) => String(n.id) === selectedNodeId),
    [nodes, selectedNodeId]
  );

  const childCount = useMemo(() => {
    if (!selectedNodeId) return 0;
    return nodes.filter((n) => String(n.parent_id) === selectedNodeId).length;
  }, [nodes, selectedNodeId]);

  if (!selected) {
    return (
      <Empty
        description="Select a WBS node from the tree, or switch to Overview"
        image={Empty.PRESENTED_IMAGE_SIMPLE}
      />
    );
  }

  const isPackage = String(selected.layer_code) === 'PACKAGE';

  const detailsTab = (
    <Descriptions size="small" column={1} bordered>
      <Descriptions.Item label="Layer">
        <Space>
          <Est3WbsLayerTag layerCode={selected.layer_code} />
          <Text type="secondary" style={{ fontSize: 12 }}>
            {WBS_LAYER_HINTS[selected.layer_code] || ''}
          </Text>
        </Space>
      </Descriptions.Item>
      <Descriptions.Item label="Children">{childCount}</Descriptions.Item>
      {selected.short_name ? (
        <Descriptions.Item label="Short name">{selected.short_name}</Descriptions.Item>
      ) : null}
      {selected.description ? (
        <Descriptions.Item label="Description">{selected.description}</Descriptions.Item>
      ) : null}
    </Descriptions>
  );

  if (compact) {
    if (!isPackage) {
      return (
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          {detailsTab}
          <Alert
            type="info"
            showIcon
            message="Select a PACKAGE node to edit CBS cost templates"
            description="Cost templates define default MAT / LAB / EQP / SUB rates when this package is plugged into an estimate."
          />
        </Space>
      );
    }

    return (
      <Tabs
        defaultActiveKey="costs"
        items={[
          { key: 'costs', label: 'CBS costs', children: (
            <EpcWbsLibraryCostTemplateGrid
              packageId={packageId}
              libraryNodeId={String(selected.id)}
              nodeLabel={`${selected.code} — ${selected.name}`}
              readOnly={readOnly}
            />
          ) },
          { key: 'details', label: 'Details', children: detailsTab },
        ]}
      />
    );
  }

  return (
    <Space direction="vertical" style={{ width: '100%' }} size="middle">
      <Est3WbsLayerTag layerCode={selected.layer_code} />
      {detailsTab}
      {isPackage ? (
        <EpcWbsLibraryCostTemplateGrid
          packageId={packageId}
          libraryNodeId={String(selected.id)}
          nodeLabel={`${selected.code} — ${selected.name}`}
          readOnly={readOnly}
        />
      ) : (
        <Paragraph type="secondary">CBS templates attach at PACKAGE layer only.</Paragraph>
      )}
    </Space>
  );
};
